import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) { setError('Missing reset token. Use the link from the email.'); return; }
    if (pwd.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (pwd !== confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { token, new_password: pwd });
      setDone(true);
      toast.success('Password reset successful — sign in with your new password.');
      setTimeout(() => navigate('/login'), 1500);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Reset failed. The link may be expired or used.';
      setError(typeof msg === 'string' ? msg : 'Reset failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#FFFDF8]">
      <Header />
      <div className="container mx-auto px-4 py-10 max-w-md">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl border border-[#E8E5DF] p-8 shadow-sm"
          data-testid="reset-password-card">
          <h1 className="text-2xl font-semibold text-[#1E1E24] mb-1" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Reset your password
          </h1>
          <p className="text-sm text-[#6B6B7A] mb-5">Pick a new password (minimum 8 characters).</p>

          {done ? (
            <div className="text-center py-4">
              <CheckCircle2 className="w-12 h-12 text-[#06D6A0] mx-auto mb-3" />
              <p className="font-semibold text-[#1E1E24]">Password updated!</p>
              <p className="text-sm text-[#6B6B7A]">Redirecting to login…</p>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2" data-testid="reset-error">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="new-pwd" className="font-semibold">New password</Label>
                  <div className="relative mt-2">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B7A]" />
                    <Input id="new-pwd" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)}
                      placeholder="Minimum 8 characters" required data-testid="reset-new-password"
                      className="pl-11 rounded-xl border-[#E8E5DF]" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="confirm-pwd" className="font-semibold">Confirm password</Label>
                  <div className="relative mt-2">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B7A]" />
                    <Input id="confirm-pwd" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                      placeholder="Re-type your new password" required data-testid="reset-confirm-password"
                      className="pl-11 rounded-xl border-[#E8E5DF]" />
                  </div>
                </div>
                <Button type="submit" disabled={loading} data-testid="reset-submit"
                  className="w-full rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white py-5">
                  {loading ? 'Resetting…' : 'Reset Password'}
                </Button>
                <p className="text-center text-xs text-[#6B6B7A]">
                  Remember your password?{' '}
                  <Link to="/login" className="text-[#FF3B6B] font-semibold hover:underline">Sign in</Link>
                </p>
              </form>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default ResetPassword;
